import React, { useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import MobileNav from "../components/MobileNav";
import Footer1 from "../components/Footer1";
import "./WorldFrameSeries.css";

const WorldFrameSeries = () => {
    const navigate = useNavigate();
    const { scrollYProgress } = useScroll();
    const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const fadeUp = {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1] } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
    };

    // Curated data sourced directly from the Official Press Release
    const frames = [
        {
            id: 1,
            num: "01",
            subject: "The World Frame -1 Elon Musk",
            title: "The Multiplanetary Vision",
            desc: "The definitive foundation of the genre, capturing the multiplanetary vision and technological milestones of the global innovator on a single, continuously stretched canvas.",
            img: "https://res.cloudinary.com/wbwvb4sk/image/upload/v1786278466/elon1.jpg" // Replace with actual image URL
        },
        {
            id: 2,
            num: "02",
            subject: "The World Frame -2 Sudha Murthy",
            title: "The Engineering Legacy",
            desc: "A breathtaking visual timeline celebrating the profound simplicity, literature, engineering legacy, and unparalleled philanthropy of one of India's most revered icons.",
            img: "https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597539/sudha_murthy_1.jpg" // Replace with actual image URL
        },
        {
            id: 3,
            num: "03",
            subject: "The World Frame -3 Narendra Modi",
            title: "The Historic Rise",
            desc: "A massive, intricately detailed pictorial journey mapping the historic rise, political milestones, and national leadership of India's Prime Minister.",
            img: "https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597528/narendra_modi.jpg" // Replace with actual image URL
        },
        {
            id: 4,
            num: "04",
            subject: "The World Frame -4 Sonu Sood",
            title: "The Relentless Beacon",
            desc: "A raw, emotional monument. It visually maps how he stepped out onto the frontlines during the world's darkest hours, serving as a reminder that true heroism lies in one's karma, not in their appearance.",
            img: "https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597537/sonu_sood_1.jpg" // Replace with actual image URL
        }
    ];

    const whatsappMessage = `Dear Studio,\n\nI am writing to inquire about the private acquisition details and exhibition schedule for the 'World Frame' Series.\n\nPlease share the curatorial dossier.`;

    return (
        <div className="world-frame-page">
            <MobileNav />

            {/* --- ACT I: CINEMATIC HERO --- */}
            <section className="wf-hero">
                <motion.div className="wf-hero-bg" style={{ y: heroY }}></motion.div>
                <div className="wf-hero-overlay"></div>
                <motion.div className="wf-hero-content" initial="hidden" animate="visible" variants={staggerContainer}>
                    <motion.span variants={fadeUp} className="wf-eyebrow">A Monumental Leap for Contemporary Fine Art</motion.span>
                    <motion.h1 variants={fadeUp} className="wf-title">The World Frame</motion.h1>
                    <motion.p variants={fadeUp} className="wf-subtitle">The Single-Frame Pictorial Biography Series</motion.p>
                    <motion.div variants={fadeUp} className="wf-gold-line"></motion.div>
                </motion.div>
            </section>

            {/* --- ACT II: THE CONCEPT --- */}
            <section className="wf-concept">
                <motion.div className="wf-concept-inner" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeUp}>
                    <h2 className="wf-concept-statement">
                        "Moving beyond traditional portraiture and multi-panel storytelling..."
                    </h2>
                    <p className="wf-concept-body">
                        This innovative concept involves meticulously stretching a single, continuous 36x48-inch canvas to capture the unbroken life journey, milestones, and impact of a global figure without a single border or partition. It is rooted in the deep philosophy that a person is defined by their karma, not just their appearance.
                    </p>
                </motion.div>
            </section>

            {/* --- ACT III: THE MASTERWORKS (SCROLLING DOSSIER) --- */}
            <section className="wf-masterworks">
                {frames.map((frame, index) => {
                    const isReverse = index % 2 !== 0;
                    return (
                        <motion.div 
                            key={frame.id} 
                            className={`wf-item ${isReverse ? 'wf-reverse' : ''}`}
                            initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={staggerContainer}
                        >
                            <motion.div className="wf-item-visual" variants={fadeUp}>
                                <div className="wf-image-container">
                                    <img src={frame.img} alt={frame.subject} />
                                    <div className="wf-image-glow"></div>
                                </div>
                            </motion.div>

                            <motion.div className="wf-item-data" variants={fadeUp}>
                                <span className="wf-item-num">Volume {frame.num}</span>
                                <h2>{frame.subject}</h2>
                                <h4 className="wf-item-theme">{frame.title}</h4>
                                <p>{frame.desc}</p>
                                
                                <div className="wf-specs">
                                    <span>Single-Frame Canvas</span>
                                    <span className="wf-dot">•</span>
                                    <span>36 x 48 Inches</span>
                                </div>
                            </motion.div>
                        </motion.div>
                    );
                })}
            </section>

            {/* --- ACT IV: THE LEGACY & INQUIRY --- */}
            <section className="wf-legacy">
                <motion.div className="wf-legacy-inner" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                    <span className="wf-eyebrow" style={{marginBottom: "20px"}}>The Archive</span>
                    <h2>A Visual Time Capsule</h2>
                    <p>
                        The true brilliance of this series lies in its permanence. Decades from now, historians and art enthusiasts will not have to piece together fragmented articles. They will stand before these canvases and trace an unbroken line of human innovation, resilience, and greatness.
                    </p>
                    
                    <div className="wf-inquiry-box">
                        <a href={`https://wa.me/919300847841?text=${encodeURIComponent(whatsappMessage)}`} target="_blank" rel="noreferrer" className="btn-wf-inquire">
                            Request Private Exhibition Dossier
                        </a>
                    </div>
                </motion.div>
            </section>

            <Footer1 />
        </div>
    );
};

export default WorldFrameSeries;